import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  FormControlLabel,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import ArchiveIcon from "@mui/icons-material/Archive";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import RefreshIcon from "@mui/icons-material/Refresh";
import UnarchiveIcon from "@mui/icons-material/Unarchive";
import { toast } from "react-toastify";

import SettingsPanel from "../../components/SettingsPanel";
import handleDateTime from "../../../../Components/Common/HandleDateTime";
import {
  masterDatabaseDeltaListApiCall,
  masterDatabaseDeltaDataApiCall,
  masterDatabaseDeltaRunListApiCall,
  masterDatabaseDeltaCreateApiCall,
  masterDatabaseDeltaUpdateApiCall,
  masterDatabaseDeltaArchiveApiCall,
  masterDatabaseDeltaDeleteApiCall,
  masterDatabaseDeltaExecuteApiCall,
} from "../../../../helpers/fakebackend_helper";

/**
 * Database Delta Manager (CRMit Task 24425).
 *
 * Replaces the old routine of opening a file in app/Database/Delta, copying the
 * SQL out, and POSTing it to /zcron/SqlMultipleDbUpdate through Postman - which
 * recorded nothing, so "has this delta been applied, and where?" was
 * unanswerable. A delta now lives as a row in ZCRM_Master.DatabaseDelta against
 * the task it belongs to, and every execution writes a DatabaseDeltaRun row per
 * target database.
 *
 * ScopeType picks the target: MASTER runs once against ZCRM_Master, COMPANY
 * loops every non-legacy company database. Execute skips databases that already
 * carry a successful run unless "force re-run" is ticked, so clicking twice is
 * a no-op rather than a re-apply.
 */

const SCOPE_OPTIONS = [
  { value: "COMPANY", label: "COMPANY - every non-legacy company database" },
  { value: "MASTER", label: "MASTER - ZCRM_Master only" },
];

// The five states summarise_runs() rolls the run history up into.
const STATUS_COLOUR = {
  PENDING: "default",
  RUNNING: "info",
  COMPLETED: "success",
  PARTIAL: "warning",
  FAILED: "error",
};

// The list mixes deltas that still need a click with deltas that are finished,
// and the finished ones pile up and drown the ones that don't. PENDING is
// everything that is not fully applied - never run, mid-run, or half-applied
// and needing a retry - which is the same "still outstanding" rule the
// /masterdatabasedelta/list docs use.
// Task 24734 - and a third tab for the ones that are outstanding on paper but
// finished in practice. "Not COMPLETED" is the right rule for what still needs
// a click, but two kinds of row can never satisfy it: a FAILED body that has
// been superseded by a rewritten delta (must never run again, must not be
// deleted - its run history is the record of what was attempted), and a
// PARTIAL whose remaining failures are understood and need no action. Both
// sat permanently at the top of the working list. Archiving files them away
// without touching the row, the SQL or the run history.
const TAB_PENDING = "pending";
const TAB_COMPLETED = "completed";
const TAB_ARCHIVED = "archived";

const isCompletedDelta = (item) =>
  String(item?.Status || "").toUpperCase() === "COMPLETED";

const isArchivedDelta = (item) => Number(item?.FlagArchived) === 1;

const initialFormState = {
  Task_Id: "",
  Title: "",
  Description: "",
  FileName: "",
  SqlCommand: "",
  ScopeType: "COMPANY",
  FlagAllowDestructive: 0,
};

const DatabaseDelta = () => {
  const [deltaList, setDeltaList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(TAB_PENDING);

  // Add / edit
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Execute confirmation
  const [executeTarget, setExecuteTarget] = useState(null);
  const [forceRerun, setForceRerun] = useState(false);
  // A COMPANY delta walks 100+ databases inside one request, so the buttons
  // have to stay disabled and say so rather than look clickable while it runs.
  const [executing, setExecuting] = useState(false);

  // Run-history drawer
  const [historyDelta, setHistoryDelta] = useState(null);
  const [historyRunList, setHistoryRunList] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Task 24684 - show only the databases that failed. Set when the drawer is
  // opened from the "(3 failed)" link, and toggleable once inside.
  const [historyFailedOnly, setHistoryFailedOnly] = useState(false);

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Task 24734 - which row's archive button is mid-flight, so only that one
  // spins rather than the whole grid.
  const [archivingId, setArchivingId] = useState(null);

  const loadList = async () => {
    setListLoading(true);
    try {
      const response = await masterDatabaseDeltaListApiCall();
      setDeltaList(response?.masterdatabasedelta_list || []);
    } catch (e) {
      toast.error("Could not load database deltas");
    }
    setListLoading(false);
  };

  useEffect(() => {
    loadList();
  }, []);

  // Archived wins over the status split: an archived row is off the working
  // list whatever state it is in, which is the whole point of archiving it.
  const { pendingRows, completedRows, archivedRows } = useMemo(() => {
    const pending = [];
    const completed = [];
    const archived = [];
    (deltaList || []).forEach((item) => {
      const row = { ...item, id: item.Id };
      if (isArchivedDelta(item)) {
        archived.push(row);
      } else if (isCompletedDelta(item)) {
        completed.push(row);
      } else {
        pending.push(row);
      }
    });
    return {
      pendingRows: pending,
      completedRows: completed,
      archivedRows: archived,
    };
  }, [deltaList]);

  const isArchivedTab = activeTab === TAB_ARCHIVED;
  const rows =
    activeTab === TAB_COMPLETED
      ? completedRows
      : isArchivedTab
      ? archivedRows
      : pendingRows;

  // ------------------------------------------------------------------
  // Add / edit
  // ------------------------------------------------------------------

  // The list response deliberately omits SqlCommand (a delta body can be tens
  // of kilobytes), so editing pulls the full row from data/{id} first.
  const handleOpenDialog = async (item = null) => {
    if (!item?.Id) {
      setEditId(null);
      setFormData(initialFormState);
      setOpenDialog(true);
      return;
    }

    setEditId(item.Id);
    setFormData({
      ...initialFormState,
      Task_Id: item.Task_Id || "",
      Title: item.Title || "",
      Description: item.Description || "",
      FileName: item.FileName || "",
      ScopeType: item.ScopeType || "COMPANY",
      FlagAllowDestructive: Number(item.FlagAllowDestructive) ? 1 : 0,
      SqlCommand: "",
    });
    setOpenDialog(true);

    try {
      const response = await masterDatabaseDeltaDataApiCall(item.Id);
      const data = response?.masterdatabasedelta_data;
      if (data) {
        setFormData({
          Task_Id: data.Task_Id || "",
          Title: data.Title || "",
          Description: data.Description || "",
          FileName: data.FileName || "",
          SqlCommand: data.SqlCommand || "",
          ScopeType: data.ScopeType || "COMPANY",
          FlagAllowDestructive: Number(data.FlagAllowDestructive) ? 1 : 0,
        });
      }
    } catch (e) {
      toast.error("Could not load the delta SQL");
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditId(null);
    setFormData(initialFormState);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.Title?.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!formData.SqlCommand?.trim()) {
      toast.error("There is no SQL to store");
      return;
    }

    const payload = {
      Task_Id: formData.Task_Id === "" ? null : formData.Task_Id,
      Title: formData.Title,
      Description: formData.Description,
      FileName: formData.FileName,
      SqlCommand: formData.SqlCommand,
      ScopeType: formData.ScopeType,
      FlagAllowDestructive: formData.FlagAllowDestructive ? 1 : 0,
    };

    setSaving(true);
    try {
      const response = editId
        ? await masterDatabaseDeltaUpdateApiCall([{ ...payload, Id: editId }])
        : await masterDatabaseDeltaCreateApiCall([payload]);

      if (response?.status === 200) {
        toast.success(response.message);
        handleCloseDialog();
        loadList();
      } else {
        toast.error(response?.message || "Could not save the delta");
      }
    } catch (err) {
      toast.error("Could not save the delta");
    }
    setSaving(false);
  };

  // ------------------------------------------------------------------
  // Execute
  // ------------------------------------------------------------------

  const loadRunHistory = async (delta) => {
    setHistoryLoading(true);
    try {
      const response = await masterDatabaseDeltaRunListApiCall(delta.Id);
      setHistoryRunList(response?.masterdatabasedeltarun_list || []);
    } catch (e) {
      toast.error("Could not load the run history");
    }
    setHistoryLoading(false);
  };

  /**
   * `databaseName` is set only when retrying one failed database from the run
   * history; a normal Execute leaves it null and runs the whole scope.
   */
  const runDelta = async (delta, databaseName = null, force = false) => {
    if (!delta?.Id) return;

    setExecuting(true);
    try {
      const response = await masterDatabaseDeltaExecuteApiCall([
        {
          Id: delta.Id,
          ...(databaseName ? { DatabaseName: databaseName } : {}),
          ...(force ? { FlagForceRerun: 1 } : {}),
        },
      ]);

      if (response?.status === 200) {
        const failed = Number(response.count_database_failed) || 0;
        const succeeded = Number(response.count_database_success) || 0;
        const message = `${response.delta_status}: ${succeeded} succeeded, ${failed} failed of ${response.count_database_target} database(s)`;
        if (failed > 0) {
          toast.warning(message);
        } else {
          toast.success(message);
        }
        loadList();
        // Keep an open history drawer honest about the run that just finished.
        if (historyDelta?.Id === delta.Id) {
          loadRunHistory(delta);
        }
      } else {
        toast.error(response?.message || "Could not execute the delta");
      }
    } catch (e) {
      toast.error("Could not execute the delta");
    }
    setExecuting(false);
  };

  const handleConfirmExecute = async () => {
    const delta = executeTarget;
    const force = forceRerun;
    setExecuteTarget(null);
    setForceRerun(false);
    await runDelta(delta, null, force);
  };

  // Task 24684 - "we need to be able to work out which ones failed so we can
  // investigate and fix". The history drawer already listed every database,
  // but on a 116-company delta the three that failed were three rows lost
  // among 113 OKs, and the error text was only a tooltip. `failedOnly` opens
  // the drawer showing just those three.
  // Task 24684 - the three views the drawer needs: what failed, those failures
  // collapsed by error message, and whichever set the table is showing.
  const historyFailures = historyRunList.filter(
    (run) => Number(run.FlagSuccess) !== 1
  );
  const historyFailureGroups = Object.values(
    historyFailures.reduce((acc, run) => {
      const message = run.ErrorMessage || "";
      if (!acc[message]) acc[message] = { message, databases: [] };
      acc[message].databases.push(run.DatabaseName);
      return acc;
    }, {})
  ).sort((a, b) => b.databases.length - a.databases.length);
  const historyVisibleRuns = historyFailedOnly
    ? historyFailures
    : historyRunList;

  const handleOpenHistory = (delta, failedOnly = false) => {
    setHistoryDelta(delta);
    setHistoryRunList([]);
    setHistoryFailedOnly(failedOnly);
    loadRunHistory(delta);
  };

  // ------------------------------------------------------------------
  // Archive
  // ------------------------------------------------------------------

  // No confirmation dialog: archiving is reversible in one click from the
  // Archived tab and changes nothing about the delta itself. Delete keeps its
  // dialog because delete is not.
  const handleArchive = async (delta, archived = true) => {
    if (!delta?.Id) return;

    setArchivingId(delta.Id);
    try {
      const response = await masterDatabaseDeltaArchiveApiCall([
        { Id: delta.Id, FlagArchived: archived ? 1 : 0 },
      ]);

      if (response?.status === 200) {
        toast.success(
          archived
            ? `Delta #${delta.Id} archived - it is off the pending list but still here`
            : `Delta #${delta.Id} restored to the working list`
        );
        loadList();
      } else {
        toast.error(response?.message || "Could not archive the delta");
      }
    } catch (e) {
      toast.error("Could not archive the delta");
    }
    setArchivingId(null);
  };

  // ------------------------------------------------------------------
  // Delete
  // ------------------------------------------------------------------

  const handleConfirmDelete = async () => {
    try {
      const response = await masterDatabaseDeltaDeleteApiCall([deleteTarget.Id]);
      if (response?.status === 200) {
        toast.success(response.message);
        loadList();
      } else {
        toast.error(response?.message || "Could not delete the delta");
      }
    } catch (e) {
      toast.error("Could not delete the delta");
    }
    setDeleteTarget(null);
  };

  // ------------------------------------------------------------------
  // Grid
  // ------------------------------------------------------------------

  const columns = [
    { field: "Id", headerName: "Id", width: 70 },
    {
      field: "Task_Id",
      headerName: "Task",
      width: 90,
      renderCell: (params) => (params.value ? `#${params.value}` : "-"),
    },
    { field: "Title", headerName: "Title", flex: 1, minWidth: 240 },
    {
      field: "ScopeType",
      headerName: "Scope",
      width: 110,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          color={params.value === "MASTER" ? "secondary" : "primary"}
          variant="outlined"
        />
      ),
    },
    {
      field: "Status",
      headerName: "Status",
      width: 120,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          color={STATUS_COLOUR[params.value] || "default"}
        />
      ),
    },
    {
      field: "CountDatabaseSuccess",
      headerName: "Databases",
      width: 140,
      sortable: false,
      renderCell: (params) => {
        const success = Number(params.row.CountDatabaseSuccess) || 0;
        const failed = Number(params.row.CountDatabaseFailed) || 0;
        const target = Number(params.row.CountDatabaseTarget) || 0;
        return (
          <Typography variant="body2">
            {success}/{target}
            {/* Task 24684 - the failure count is the question ("which ones?"),
                so it is now the answer's front door: click it and the run
                history opens filtered to just those databases, error text
                and per-database retry included. */}
            {failed > 0 && (
              <Tooltip title={`Show the ${failed} that failed`}>
                <Box
                  component="span"
                  role="button"
                  tabIndex={0}
                  sx={{
                    color: "error.main",
                    ml: 0.5,
                    cursor: "pointer",
                    textDecoration: "underline",
                    "&:hover": { fontWeight: 700 },
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleOpenHistory(params.row, true);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      handleOpenHistory(params.row, true);
                    }
                  }}
                >
                  ({failed} failed)
                </Box>
              </Tooltip>
            )}
          </Typography>
        );
      },
    },
    {
      field: "LastRunDateTime",
      headerName: "Last Run",
      width: 180,
      renderCell: (params) =>
        params.value ? handleDateTime(params.value) : "Never",
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 215,
      sortable: false,
      renderCell: (params) => (
        <Box>
          {/* Execute is deliberately absent on the Archived tab. An archived
              delta is one that has been declared as needing no further action;
              if that turns out to be wrong, restore it first and the button
              comes back. */}
          {!isArchivedTab && (
            <Tooltip title="Execute this delta">
              <span>
                <IconButton
                  color="success"
                  size="small"
                  disabled={executing}
                  onClick={() => {
                    setExecuteTarget(params.row);
                    setForceRerun(false);
                  }}
                >
                  <PlayArrowIcon />
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Run history">
            <IconButton
              size="small"
              onClick={() => handleOpenHistory(params.row)}
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              size="small"
              onClick={() => handleOpenDialog(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              isArchivedTab
                ? `Restore to the working list${
                    params.row.ArchivedDateTime
                      ? ` (archived ${handleDateTime(
                          params.row.ArchivedDateTime
                        )})`
                      : ""
                  }`
                : "Archive - takes it off this list without deleting it or its run history"
            }
          >
            <span>
              <IconButton
                color={isArchivedTab ? "primary" : "warning"}
                size="small"
                disabled={archivingId === params.row.Id}
                onClick={() => handleArchive(params.row, !isArchivedTab)}
              >
                {isArchivedTab ? <UnarchiveIcon /> : <ArchiveIcon />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              size="small"
              onClick={() => setDeleteTarget(params.row)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <SettingsPanel
      title="Database Deltas"
      description="SQL delta scripts stored in the master DB against the task they belong to, and run from here instead of being pasted into Postman. Every execution records a per-database result, so a half-applied delta shows as PARTIAL and only the databases that failed need retrying. Anything that will never reach COMPLETED - a superseded FAILED body, a PARTIAL whose remaining failures need no action - can be archived off the Pending list without losing the row or its history."
      tabs={[
        { value: TAB_PENDING, label: "Pending", count: pendingRows.length },
        {
          value: TAB_COMPLETED,
          label: "Completed",
          count: completedRows.length,
        },
        {
          value: TAB_ARCHIVED,
          label: "Archived",
          count: archivedRows.length,
        },
      ]}
      value={activeTab}
      onChange={setActiveTab}
    >
      <Box sx={{ p: 3, width: "100%", minWidth: 0, maxWidth: "100%" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Database Delta Manager
          </Typography>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadList}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Delta
            </Button>
          </Box>
        </Box>

        {isArchivedTab && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Filed away as needing no further action. Nothing here has been
            changed or deleted - the SQL and the full run history are intact,
            and Restore puts a row back on the working list with its Execute
            button.
          </Typography>
        )}

        {executing && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
            <CircularProgress size={18} />
            <Typography variant="body2">
              Running - a company delta walks every database, so this can take a
              while. Leave the screen open.
            </Typography>
          </Box>
        )}

        <Paper elevation={3} sx={{ height: 600, width: "100%" }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={listLoading}
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
            }}
            disableRowSelectionOnClick
            sx={{ border: 0 }}
          />
        </Paper>

        {/* ---------------------------------------------------------- */}
        {/* Add / edit                                                 */}
        {/* ---------------------------------------------------------- */}
        <Dialog
          open={openDialog}
          onClose={handleCloseDialog}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            {editId ? `Edit Delta #${editId}` : "Add New Delta"}
          </DialogTitle>
          <form onSubmit={handleSubmit}>
            <DialogContent dividers>
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  margin="dense"
                  label="CRMit Task Id"
                  name="Task_Id"
                  value={formData.Task_Id}
                  onChange={handleInputChange}
                  sx={{ width: 200 }}
                />
                <TextField
                  margin="dense"
                  label="Title"
                  name="Title"
                  value={formData.Title}
                  onChange={handleInputChange}
                  fullWidth
                  required
                />
              </Box>
              <TextField
                margin="dense"
                label="Description (what it changes and why)"
                name="Description"
                value={formData.Description}
                onChange={handleInputChange}
                fullWidth
                multiline
                rows={2}
              />
              <Box sx={{ display: "flex", gap: 2 }}>
                <TextField
                  margin="dense"
                  select
                  label="Scope"
                  name="ScopeType"
                  value={formData.ScopeType}
                  onChange={handleInputChange}
                  sx={{ minWidth: 340 }}
                >
                  {SCOPE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  margin="dense"
                  label="Source file (e.g. 20260908_2500_Something.sql)"
                  name="FileName"
                  value={formData.FileName}
                  onChange={handleInputChange}
                  fullWidth
                />
              </Box>
              <TextField
                margin="dense"
                label="SQL"
                name="SqlCommand"
                value={formData.SqlCommand}
                onChange={handleInputChange}
                fullWidth
                required
                multiline
                minRows={12}
                InputProps={{
                  sx: { fontFamily: "monospace", fontSize: "0.85rem" },
                }}
                helperText="Multi-statement scripts are fine - the statements are split on execute. USE statements are ignored; the target database is selected for you."
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!formData.FlagAllowDestructive}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        FlagAllowDestructive: e.target.checked ? 1 : 0,
                      }))
                    }
                  />
                }
                label="Allow destructive statements (DELETE / TRUNCATE / DROP) - blocked otherwise"
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} color="inherit">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={saving}
              >
                {editId ? "Save Changes" : "Create"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* ---------------------------------------------------------- */}
        {/* Execute confirmation                                       */}
        {/* ---------------------------------------------------------- */}
        <Dialog
          open={!!executeTarget}
          onClose={() => setExecuteTarget(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Execute this delta?</DialogTitle>
          <DialogContent dividers>
            <DialogContentText component="div">
              <strong>{executeTarget?.Title}</strong>
              <Box sx={{ mt: 1 }}>
                {executeTarget?.ScopeType === "MASTER"
                  ? "Runs once against ZCRM_Master."
                  : "Runs against every non-legacy company database."}
              </Box>
              <Box sx={{ mt: 1 }}>
                Databases that already carry a successful run are skipped, so
                this is safe to click on a partially applied delta.
              </Box>
            </DialogContentText>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Checkbox
                  checked={forceRerun}
                  onChange={(e) => setForceRerun(e.target.checked)}
                />
              }
              label="Force re-run (re-apply to databases that already succeeded)"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExecuteTarget(null)} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="success"
              disabled={executing}
              onClick={handleConfirmExecute}
            >
              Execute
            </Button>
          </DialogActions>
        </Dialog>

        {/* ---------------------------------------------------------- */}
        {/* Delete confirmation                                        */}
        {/* ---------------------------------------------------------- */}
        <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
          <DialogTitle>Delete this delta?</DialogTitle>
          <DialogContent dividers>
            <DialogContentText>
              &ldquo;{deleteTarget?.Title}&rdquo; and its run history will be
              removed. This does not undo any SQL that has already run.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteTarget(null)} color="inherit">
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={handleConfirmDelete}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* ---------------------------------------------------------- */}
        {/* Run history                                                */}
        {/* ---------------------------------------------------------- */}
        <Drawer
          anchor="right"
          open={!!historyDelta}
          onClose={() => setHistoryDelta(null)}
          PaperProps={{ sx: { width: { xs: "100%", md: 760 } } }}
        >
          <Box sx={{ p: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 2,
              }}
            >
              <Box>
                <Typography variant="h6">Run History</Typography>
                <Typography variant="body2" color="text.secondary">
                  {historyDelta?.Title}
                </Typography>
              </Box>
              <IconButton size="small" onClick={() => setHistoryDelta(null)}>
                <CloseIcon />
              </IconButton>
            </Box>

            {historyLoading && <CircularProgress size={22} />}

            {!historyLoading && historyRunList.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                This delta has never been run.
              </Typography>
            )}

            {/* Task 24684 - failures first, and in full. The error text used
                to be a tooltip on a chip buried in a 116-row table, so the
                one thing you opened this drawer for was the hardest thing in
                it to reach. Grouping by message matters as much as listing
                them: 113 identical "Unknown column" errors are one bug, not
                113, and seeing that immediately is the difference between a
                five-minute fix and an afternoon. */}
            {!historyLoading && historyFailures.length > 0 && (
              <Box
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "error.light",
                  // Derived from the palette rather than "error.lighter",
                  // which is not a key MUI's default palette defines - it
                  // would resolve to an invalid colour and quietly paint
                  // nothing.
                  bgcolor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(244, 67, 54, 0.12)"
                      : "rgba(244, 67, 54, 0.06)",
                }}
              >
                <Typography
                  variant="subtitle2"
                  color="error.main"
                  sx={{ mb: 1 }}
                >
                  {historyFailures.length} of {historyRunList.length} failed
                  {historyFailureGroups.length > 1
                    ? ` — ${historyFailureGroups.length} distinct errors`
                    : historyFailureGroups.length === 1
                    ? " — all with the same error"
                    : ""}
                </Typography>
                {historyFailureGroups.map((group) => (
                  <Box key={group.message} sx={{ mb: 1 }}>
                    <Typography
                      variant="caption"
                      sx={{ fontFamily: "monospace", display: "block" }}
                    >
                      {group.message || "(no error message recorded)"}
                    </Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontFamily: "monospace", display: "block" }}
                    >
                      {group.databases.slice(0, 8).join(", ")}
                      {group.databases.length > 8
                        ? ` … and ${group.databases.length - 8} more`
                        : ""}
                      {"  "}({group.databases.length})
                    </Typography>
                  </Box>
                ))}
                <Button
                  size="small"
                  onClick={() => setHistoryFailedOnly((prev) => !prev)}
                >
                  {historyFailedOnly
                    ? `Show all ${historyRunList.length} databases`
                    : `Show only the ${historyFailures.length} that failed`}
                </Button>
              </Box>
            )}

            {!historyLoading && historyRunList.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Database</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell align="right">Duration</TableCell>
                    <TableCell>When</TableCell>
                    <TableCell align="right">Retry</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyVisibleRuns.map((run) => (
                    <TableRow key={run.Id}>
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {run.DatabaseName}
                      </TableCell>
                      <TableCell>{run.Company_BusinessName || "-"}</TableCell>
                      <TableCell>
                        {Number(run.FlagSuccess) === 1 ? (
                          <Chip size="small" color="success" label="OK" />
                        ) : (
                          <Tooltip title={run.ErrorMessage || ""}>
                            <Chip size="small" color="error" label="FAILED" />
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        {run.DurationMs != null ? `${run.DurationMs} ms` : "-"}
                      </TableCell>
                      <TableCell>
                        {run.RunDateTime ? handleDateTime(run.RunDateTime) : "-"}
                      </TableCell>
                      <TableCell align="right">
                        {/* A single failed company is retried on its own rather
                            than by re-running the whole scope. Forced, because
                            the failed row is exactly the one to re-attempt. */}
                        {Number(run.FlagSuccess) !== 1 && (
                          <Tooltip title="Re-run against this database only">
                            <span>
                              <IconButton
                                size="small"
                                color="success"
                                disabled={executing}
                                onClick={() =>
                                  runDelta(historyDelta, run.DatabaseName, true)
                                }
                              >
                                <PlayArrowIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}

            {/* Task 24684 - the old "Errors" list lived here, below the full
                table. It is now the grouped summary at the top of the drawer,
                where it is the first thing read rather than the last. */}
          </Box>
        </Drawer>
      </Box>
    </SettingsPanel>
  );
};

export default DatabaseDelta;
